import type { HoppCollection } from "@hoppscotch/data"
import type { TeamCollection } from "~/helpers/teams/TeamCollection"

export function personalRequestRoot(
  roots: HoppCollection[],
  path: string,
  requestRefID?: string
): HoppCollection | undefined {
  // Prefer stable request identity: folder positions can change after sorting.
  const contains = (collection: HoppCollection): boolean =>
    collection.requests.some(
      (request) =>
        ("_ref_id" in request && request._ref_id === requestRefID) ||
        ("id" in request && request.id === requestRefID)
    ) || collection.folders.some(contains)
  if (requestRefID) return roots.find(contains)
  return roots[Number(path.split("/")[0])]
}

export function personalCollectionRoot(roots: HoppCollection[], id: string) {
  const contains = (collection: HoppCollection): boolean =>
    collection._ref_id === id ||
    collection.id === id ||
    collection.folders.some(contains)
  return roots.find(contains)
}

export function teamRequestRoot(
  roots: TeamCollection[],
  path: string,
  requestID: string
): TeamCollection | undefined {
  const collectionID = path.split("/")[0]
  const contains = (collection: TeamCollection): boolean =>
    collection.id === collectionID ||
    !!collection.requests?.some((request) => request.id === requestID) ||
    !!collection.children?.some(contains)
  return roots.find(contains)
}

export function collectionEnvironmentIds(data: unknown): string[] {
  if (!data || typeof data !== "object") return []
  const ids = (data as { environmentIds?: unknown }).environmentIds
  return Array.isArray(ids) && ids.every((id) => typeof id === "string")
    ? ids
    : []
}

let reconcileSelection: (() => void) | undefined
let reconciling = false

export function registerCollectionEnvironmentReconciler(reconcile: () => void) {
  reconcileSelection = reconcile
  return () => {
    if (reconcileSelection === reconcile) reconcileSelection = undefined
  }
}

export function ensureCollectionEnvironmentSelection() {
  if (reconciling) return
  reconciling = true
  try {
    reconcileSelection?.()
  } finally {
    reconciling = false
  }
}

/** Stores IDs only; environment values and credentials never enter this cache. */
export class CollectionEnvironmentSelection {
  private previousKey: string | undefined
  private filtered = false
  private previousSelection: string | null = null
  private awaitingRestore = false
  private pendingSelection: string | undefined

  constructor(public remembered: Record<string, string | null>) {}

  reconcile(
    defaultKey: string,
    collectionKey: string | undefined,
    allowed: string[] | null,
    available: string[],
    selected: string | null,
    pending = false
  ): string | null {
    if (pending) {
      if (!this.filtered && selected !== null) this.pendingSelection = selected
      if (!this.filtered && this.previousKey)
        this.remembered[this.previousKey] = this.previousSelection
      this.awaitingRestore = true
      return null
    }
    const filtered = !!allowed?.length
    const key = filtered ? collectionKey! : defaultKey
    const changed = key !== this.previousKey || filtered !== this.filtered
    const valid = (id: string | null) =>
      id !== null &&
      available.includes(id) &&
      (!filtered || allowed!.includes(id))

    let next = selected
    if (changed || this.awaitingRestore) {
      if (filtered || this.filtered || this.awaitingRestore) {
        if (
          !this.filtered &&
          filtered &&
          this.previousKey === defaultKey &&
          !this.awaitingRestore
        )
          this.remembered[defaultKey] = selected
        const saved =
          (!filtered ? this.pendingSelection : undefined) ??
          this.remembered[key] ??
          null
        next = valid(saved) ? saved : null
        // Initial environment/collection hydration may complete in either order.
        this.awaitingRestore = saved !== null && !available.includes(saved)
        if (!this.awaitingRestore) this.pendingSelection = undefined
      }
    } else if (selected !== this.previousSelection) {
      next = filtered && !valid(selected) ? null : selected
      this.remembered[key] = next
    } else if (filtered && selected !== null && !valid(selected)) {
      next = null
      this.remembered[key] = null
    }

    if (filtered && next !== null && !valid(next)) next = null
    this.previousKey = key
    this.filtered = filtered
    this.previousSelection = next
    return next
  }
}
